import { Link } from "react-router-dom";
import { MapPin, Clock, User, ArrowSquareOut, Briefcase, Lightning } from "@phosphor-icons/react";

/**
 * Compact overlay card for the tracker pages — shows the operative's currently
 * in-progress job + the next upcoming one, with a one-click Google Maps link
 * so the admin can quickly sanity-check the tracker pin against the booked
 * address.
 *
 * Pass `data` = the response from /api/in-house/team/{id}/current-job.
 * Set `variant="floating"` to absolute-position over the iframe; default is
 * inline. Set `compact` to true for the tiny version used in overview cards.
 */
export default function CurrentJobOverlay({ data, variant = "floating", compact = false, dtPrefix = "current-job" }) {
  if (!data) return null;
  const { current, next } = data;
  if (!current && !next) {
    if (compact) return null;
    return (
      <div
        className={variant === "floating"
          ? "absolute top-3 right-3 max-w-xs z-10 bg-card/95 backdrop-blur-sm border border-border rounded-lg p-3 shadow-lg text-xs"
          : "border border-border rounded-md p-3 bg-card text-xs"}
        data-testid={`${dtPrefix}-empty`}
      >
        <div className="flex items-center gap-1.5 text-muted-foreground">
          <Briefcase size={12} weight="duotone" /> No jobs scheduled for today.
        </div>
      </div>
    );
  }

  const cls = variant === "floating"
    ? "absolute top-3 right-3 max-w-xs z-10 bg-card/95 backdrop-blur-md border border-border rounded-lg shadow-lg overflow-hidden"
    : "border border-border rounded-md bg-card overflow-hidden";

  return (
    <div className={cls} data-testid={dtPrefix}>
      {current && <JobBlock job={current} live={true} dtPrefix={`${dtPrefix}-current`} compact={compact} />}
      {next && (
        <div className={current ? "border-t border-border" : ""}>
          <JobBlock job={next} live={false} dtPrefix={`${dtPrefix}-next`} compact={compact} />
        </div>
      )}
    </div>
  );
}

function JobBlock({ job, live, dtPrefix, compact }) {
  const gmapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(job.address || "")}`;
  return (
    <div className={compact ? "p-2 text-[11px] space-y-1" : "p-3 text-xs space-y-1.5"} data-testid={dtPrefix}>
      <div className="flex items-center gap-1.5">
        {live ? (
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-300 font-semibold text-[10px]">
            <span className="relative flex h-1.5 w-1.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500"></span>
            </span>
            Live
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300 font-semibold text-[10px]">
            <Clock size={9} weight="fill" /> Next
          </span>
        )}
        {job.kind === "emergency" && (
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-300 font-semibold text-[10px]">
            <Lightning size={9} weight="fill" /> Emergency
          </span>
        )}
        <span className="font-mono text-muted-foreground text-[10px]">{job.reference}</span>
      </div>
      <p className={compact ? "font-semibold leading-tight line-clamp-1" : "font-semibold leading-tight"} data-testid={`${dtPrefix}-title`}>{job.title}</p>
      <p className="flex items-start gap-1 text-muted-foreground leading-tight">
        <MapPin size={11} weight="duotone" className="shrink-0 mt-0.5" />
        <span className={compact ? "line-clamp-2" : ""} data-testid={`${dtPrefix}-address`}>{job.address || "(no address)"}</span>
      </p>
      {job.client_name && !compact && (
        <p className="flex items-center gap-1 text-muted-foreground">
          <User size={11} weight="duotone" /> {job.client_name}
        </p>
      )}
      <div className="flex items-center justify-between gap-2 pt-1">
        <span className="flex items-center gap-1 text-muted-foreground">
          <Clock size={11} weight="duotone" /> {job.start_time || "—"}{job.end_time ? `–${job.end_time}` : ""}
        </span>
        <div className="flex items-center gap-2 shrink-0">
          {!compact && job.address && (
            <a
              href={gmapsUrl}
              target="_blank"
              rel="noreferrer noopener"
              className="text-blue-600 hover:underline inline-flex items-center gap-0.5 font-medium"
              data-testid={`${dtPrefix}-gmaps`}
              title="Open this address in Google Maps to compare with the tracker"
            >
              <ArrowSquareOut size={10} /> Map
            </a>
          )}
          {!compact && (
            <Link
              to={`/${job.kind === "emergency" ? "emergency-jobs" : "jobs"}/${job.id}`}
              className="text-blue-600 hover:underline font-medium"
              data-testid={`${dtPrefix}-link`}
            >
              Job →
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
